import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000Alert from '../../../u2000Core/components/controls/U2000Alert';
import U2000AutoCompleteInput from '../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput';
import U2000ComboBox from '../../../u2000Core/components/formControls/combobox/U2000ComboBox';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { U3129ProjectSearchPanel } from '../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells';
import { NS, U3147ROLES } from '../../U3147';
import { useU3147Context } from '../../U3147Context';
import { useU3147TimerHandlers } from '../../U3147UseTimerHandlers';
import { useU3147HonorariumTemplateSync } from '../../U3147UseHonorariumTemplateSync';
import {
  useU3147FindProjectHandlers,
  useU3147FindSubprojectHandlers,
  useU3147FindTasksForProjectHandlers,
} from '../../U3147UseFindProjectSubprojectHandlers';
import {
  u3147FormatProjectLabel,
  u3147FormatRecentProjectChipLabel,
  u3147FormatSubprojectLabel,
  u3147FormatTaskLabel,
  u3147IsAdmIncludeSubProjectEnabled,
  u3147RecentProjectId,
  u3147ResolveTaskIdForRecentApply,
  u3147ResolveTimerProjectId,
  type U3147FindProjectItem,
  type U3147FindSubprojectItem,
  type U3147RecentProjectItem,
  type U3147TaskItem,
  type U3147TimeSession,
} from '../../U3147Service';
import { u3147RecentProjectChipKey } from '../../tools/u3147HonorariumTemplateSync';
import {
  u3147ElapsedSecondsFromStartedAt,
  u3147FormatElapsedHms,
  u3147TodayIsoDate,
} from '../../tools/u3147FormatDuration';

type TimerFormRow = {
  project: U3147FindProjectItem | null;
  subproject: U3147FindSubprojectItem | null;
  taskId: string;
  note: string;
};

const emptyTimerForm = (): TimerFormRow => ({
  project: null,
  subproject: null,
  taskId: '',
  note: '',
});

const timerFormBusinessHandlers = { newRecord: () => emptyTimerForm() };

function u3147TimerRowFromRecentItem(item: U3147RecentProjectItem): TimerFormRow | null {
  const projectId = u3147RecentProjectId(item);
  if (!projectId) return null;

  const subprojectId = item.subprojectId?.trim() || null;
  const project: U3147FindProjectItem = {
    id: projectId,
    projetNumber: item.projetNumber,
    projetName: item.projetName,
    customerCode: item.customerCode,
    customerName: item.customerName,
    includeSubProjet: item.includeSubProjet ?? !!subprojectId,
  };

  return {
    project,
    subproject: subprojectId
      ? {
          id: subprojectId,
          projectId,
          subProjetNumber: item.subProjetNumber,
          subProjetName: item.subProjetName,
        }
      : null,
    taskId: item.taskId?.trim() ?? '',
    note: item.note?.trim() ?? '',
  };
}

function u3147SameTaskList(a: U3147TaskItem[], b: U3147TaskItem[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((task, index) => task.id === b[index]?.id);
}

export interface U3147TimerCardProps {
  runningSession: U3147TimeSession | null;
  daySessions?: U3147TimeSession[];
  onTimerChanged: (dateOverride?: string) => void;
  onTimerStarted?: (session: U3147TimeSession) => void;
  onTimerStopped?: () => void;
}

const U3147TimerCard: React.FC<U3147TimerCardProps> = ({
  runningSession,
  daySessions = [],
  onTimerChanged,
  onTimerStarted,
  onTimerStopped,
}) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { initLU, selectedDate, setOpenSessionId, roundingMinutes, organizationTimezone } = useU3147Context();
  const {
    favoriteProjects,
    isFormRowFavorite,
    addFavorite,
    removeFavoriteByKey,
    syncFavoriteNote,
  } = useU3147HonorariumTemplateSync(i18n.language);
  const { busy, startTimer, stopTimer } = useU3147TimerHandlers();
  const { search: searchProject } = useU3147FindProjectHandlers();
  const { fetchTasks } = useU3147FindTasksForProjectHandlers();
  const [tasks, setTasks] = useState<U3147TaskItem[]>([]);
  const [elapsedSec, setElapsedSec] = useState(0);
  const setProjectSelectionRef = useRef<((item: U3147FindProjectItem | null) => void) | null>(null);
  const setProjectInputValueRef = useRef<((value: string) => void) | null>(null);
  const setSubprojectSelectionRef = useRef<((item: U3147FindSubprojectItem | null) => void) | null>(null);
  const setSubprojectInputValueRef = useRef<((value: string) => void) | null>(null);
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const lastAppliedTimerRowRef = useRef<TimerFormRow | null>(null);

  const admIncludeSubProjectEnabled = u3147IsAdmIncludeSubProjectEnabled(initLU.personalization);

  const [formState, formStateDispatch] = useReducer<U2000FormState, [action: { type: string; data?: unknown }]>(
    u2000FormState,
    u2000IntFormState,
  );
  const formStateHandlers = useU2000FormStateHandlers<TimerFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: timerFormBusinessHandlers,
  });

  useEffect(() => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setData(emptyTimerForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init formulaire au montage
  }, []);

  const data = formStateHandlers.getData();
  const project = data?.project ?? null;
  const subproject = data?.subproject ?? null;
  const projectIdForCapture = u3147ResolveTimerProjectId(project);
  const hasProjectSelected = !!projectIdForCapture;
  const hasTaskSelected = !!data?.taskId?.trim();

  useEffect(() => {
    if (hasProjectSelected && data) {
      lastAppliedTimerRowRef.current = data;
    }
  }, [data, hasProjectSelected]);

  const projectIdRef = useRef<string | null>(null);
  projectIdRef.current = projectIdForCapture || null;
  const getProjectId = useCallback(() => projectIdRef.current, []);
  const findSubprojectHandlers = useU3147FindSubprojectHandlers({ getProjectId });

  const isRunning = !!runningSession;
  const canTrack = hasRole(U3147ROLES.TRACK);
  const isSelectedToday = selectedDate.slice(0, 10) === u3147TodayIsoDate();
  const canStartTimer = hasProjectSelected && hasTaskSelected && canTrack && isSelectedToday && !busy;
  const captureReadonly = !canTrack || busy || !isSelectedToday;

  useEffect(() => {
    const pid = projectIdForCapture;
    if (!pid) {
      setTasks((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    void fetchTasksRef.current({
      projectId: pid,
      subprojectId: subproject?.id?.trim() || null,
    }).then((list) => {
      if (cancelled) return;
      setTasks((prev) => (u3147SameTaskList(prev, list) ? prev : list));
    });
    return () => {
      cancelled = true;
    };
  }, [projectIdForCapture, subproject?.id]);

  const currentFormIsFavorite = useMemo(() => {
    if (!data) return false;
    return isFormRowFavorite({
      project: data.project ?? null,
      subproject: data.subproject ?? null,
      taskId: data.taskId ?? '',
    });
  }, [data, isFormRowFavorite]);

  useEffect(() => {
    if (!hasProjectSelected || !currentFormIsFavorite || !data) return;
    syncFavoriteNote({
      project: data.project ?? null,
      subproject: data.subproject ?? null,
      taskId: data.taskId ?? '',
      note: data.note ?? '',
    });
  }, [data?.note, hasProjectSelected, currentFormIsFavorite, data, syncFavoriteNote]);

  const resolveTimerFormRow = useCallback((): TimerFormRow | null => {
    const current = formStateHandlers.getData();
    if (current && u3147ResolveTimerProjectId(current.project)) {
      return current;
    }
    const fallback = lastAppliedTimerRowRef.current;
    if (fallback && u3147ResolveTimerProjectId(fallback.project)) {
      return fallback;
    }
    return null;
  }, [formStateHandlers]);

  useEffect(() => {
    if (!isRunning || !runningSession?.startedAt) {
      setElapsedSec(0);
      return;
    }
    const tick = () => setElapsedSec(u3147ElapsedSecondsFromStartedAt(runningSession.startedAt));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [isRunning, runningSession?.startedAt]);

  const forgottenHours = isRunning ? elapsedSec / 3600 : 0;
  const showForgottenBanner = isRunning && forgottenHours >= 8;

  const onSearchProject = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => searchProject(criterias),
    [searchProject],
  );

  const onSearchSubproject = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findSubprojectHandlers.search(criterias),
    [findSubprojectHandlers],
  );

  const extractProjectData = useCallback((item: U3147FindProjectItem | null) => {
    if (item) {
      return [
        { fieldName: 'project', value: item },
        { fieldName: 'subproject', value: null },
        { fieldName: 'taskId', value: '' },
      ];
    }
    return [
      { fieldName: 'project', value: null },
      { fieldName: 'subproject', value: null },
      { fieldName: 'taskId', value: '' },
    ];
  }, []);

  const extractSubprojectData = useCallback((item: U3147FindSubprojectItem | null) => {
    if (item) {
      return [{ fieldName: 'subproject', value: item }, { fieldName: 'taskId', value: '' }];
    }
    return [{ fieldName: 'subproject', value: null }, { fieldName: 'taskId', value: '' }];
  }, []);

  const displayProject = useCallback((item: Record<string, unknown>) => u3147FormatProjectLabel(item as unknown as U3147FindProjectItem), []);

  const displaySubproject = useCallback((item: Record<string, unknown>) => {
    const sp = item as unknown as U3147FindSubprojectItem;
    return sp?.subProjetNumber ?? sp?.id ?? '';
  }, []);

  const formatTaskLabel = useCallback(
    (item: U3147TaskItem) => u3147FormatTaskLabel(item, i18n.language),
    [i18n.language],
  );

  const findTaskByValue = useCallback(
    (value: string, list: U3147TaskItem[]) =>
      list.find((task) => String(task.id) === String(value)) ?? null,
    [],
  );

  const bindProjectSelection = useCallback(
    (setCurrentSelection: (item: U3147FindProjectItem | null) => void, setInputValue: (value: string) => void) => {
      setProjectSelectionRef.current = setCurrentSelection;
      setProjectInputValueRef.current = setInputValue;
    },
    [],
  );

  const bindSubprojectSelection = useCallback(
    (setCurrentSelection: (item: U3147FindSubprojectItem | null) => void, setInputValue: (value: string) => void) => {
      setSubprojectSelectionRef.current = setCurrentSelection;
      setSubprojectInputValueRef.current = setInputValue;
    },
    [],
  );

  const syncAutocompleteSelection = useCallback((next: TimerFormRow) => {
    setProjectSelectionRef.current?.(next.project);
    setProjectInputValueRef.current?.(u3147FormatProjectLabel(next.project));

    if (next.subproject) {
      setSubprojectSelectionRef.current?.(next.subproject);
      setSubprojectInputValueRef.current?.(u3147FormatSubprojectLabel(next.subproject));
    } else {
      setSubprojectSelectionRef.current?.(null);
      setSubprojectInputValueRef.current?.('');
    }
  }, []);

  const applyRecent = async (item: U3147RecentProjectItem) => {
    if (!isSelectedToday) return;
    const base = u3147TimerRowFromRecentItem(item);
    if (!base?.project || !u3147ResolveTimerProjectId(base.project)) return;

    const taskList = await fetchTasksRef.current({
      projectId: u3147ResolveTimerProjectId(base.project),
      subprojectId: base.subproject?.id?.trim() || null,
    });

    const next: TimerFormRow = {
      ...base,
      taskId: u3147ResolveTaskIdForRecentApply(item, taskList),
    };

    lastAppliedTimerRowRef.current = next;
    setTasks((prev) => (u3147SameTaskList(prev, taskList) ? prev : taskList));
    formStateHandlers.setData(next);
    syncAutocompleteSelection(next);
  };

  const handleStart = async () => {
    const row = resolveTimerFormRow();
    const projectId = row?.project ? u3147ResolveTimerProjectId(row.project) : '';
    const taskId = row?.taskId?.trim() ?? '';
    if (!projectId || !taskId || !canTrack || !isSelectedToday) {
      if (canTrack && isSelectedToday && (!projectId || !taskId)) {
        toast.error(
          t('msgTimerStartNeedsProjectAndTask', {
            ns: NS,
            defaultValue: 'Sélectionnez un projet et une tâche avant de démarrer le chronomètre.',
          }),
        );
      }
      return;
    }
    const hadOpen = isRunning;
    const result = await startTimer(
      {
        projectId,
        subprojectId: row?.subproject?.id?.trim() || null,
        taskId,
        note: row?.note?.trim() || null,
      },
      { hadOpenSession: hadOpen },
    );
    if (result) {
      setOpenSessionId(result.id);
      onTimerStarted?.(result);
      onTimerChanged(result.workDate?.slice(0, 10));
    }
  };

  const handleStop = async () => {
    if (!runningSession?.id || !runningSession.startedAt || !canTrack) return;
    const stoppedAtMs = Date.now();
    const result = await stopTimer(runningSession.id, {
      roundingMinutes,
      startedAtIso: runningSession.startedAt,
      organizationTimezone,
      stoppedAtMs,
      existingSessions: daySessions,
    });
    if (result) {
      setOpenSessionId(null);
      onTimerStopped?.();
      onTimerChanged();
    }
  };

  const renderProjectPanel = useCallback(
    (props: React.ComponentProps<typeof U3129ProjectSearchPanel>) => <U3129ProjectSearchPanel {...props} ns="u3129" />,
    [],
  );

  const handleClearFormFields = useCallback(() => {
    const next = emptyTimerForm();
    lastAppliedTimerRowRef.current = null;
    setTasks([]);
    formStateHandlers.setData(next);
    syncAutocompleteSelection(next);
  }, [formStateHandlers, syncAutocompleteSelection]);

  const handleAddFavorite = useCallback(() => {
    const row = resolveTimerFormRow();
    if (!row?.project || !u3147ResolveTimerProjectId(row.project)) return;
    addFavorite({
      project: row.project,
      subproject: row.subproject,
      taskId: row.taskId ?? '',
      note: row.note ?? '',
      task: row.taskId?.trim()
        ? tasks.find((task) => String(task.id) === String(row.taskId)) ?? null
        : null,
    });
  }, [resolveTimerFormRow, tasks, addFavorite]);

  const handleRemoveFavoriteChip = useCallback(
    (identityKey: string) => {
      removeFavoriteByKey(identityKey);
    },
    [removeFavoriteByKey],
  );

  const noteFieldActions = useMemo(() => {
    if (!canTrack) return undefined;
    return (
      <div className="flex shrink-0 items-center gap-2">
        <U2000ActionBtn
          label={t('btnStart', { ns: NS, defaultValue: 'Démarrer' })}
          handleClick={() => void handleStart()}
          disabled={!canStartTimer}
          className="bg-green-600! hover:bg-green-700!"
          title={t('btnStart', { ns: NS, defaultValue: 'Démarrer' })}
        />
        {hasProjectSelected && !currentFormIsFavorite && isSelectedToday && (
          <button
            type="button"
            onClick={handleAddFavorite}
            title={t('btnAddFavorite', { ns: NS, defaultValue: 'Ajouter aux favoris' })}
            aria-label={t('btnAddFavorite', { ns: NS, defaultValue: 'Ajouter aux favoris' })}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-lg font-semibold text-blue-700 transition-colors hover:bg-blue-100"
          >
            +
          </button>
        )}
        {isSelectedToday && (
          <button
            type="button"
            onClick={handleClearFormFields}
            disabled={!hasProjectSelected && !data?.taskId?.trim() && !data?.note?.trim()}
            title={t('btnClearTimerForm', { ns: NS, defaultValue: 'Vider les champs' })}
            aria-label={t('btnClearTimerForm', { ns: NS, defaultValue: 'Vider les champs' })}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-lg font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ×
          </button>
        )}
      </div>
    );
  }, [
    canTrack,
    t,
    handleStart,
    busy,
    canStartTimer,
    isSelectedToday,
    hasProjectSelected,
    handleAddFavorite,
    handleClearFormFields,
    currentFormIsFavorite,
    data?.taskId,
    data?.note,
  ]);

  return (
    <div className="sticky top-0 z-10 rounded-t-lg rounded-b-none border border-b-0 border-gray-200 bg-white p-4 shadow-xs">
      {showForgottenBanner && (
        <div className="mb-4">
          <U2000Alert
            isOpen
            type="warning"
            message={t('msgForgottenTimer', {
              ns: NS,
              hours: Math.floor(forgottenHours),
              defaultValue: "Un chronomètre tourne depuis {{hours}} h — l'arrêter ?",
            })}
          />
        </div>
      )}

      {isRunning && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-emerald-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">
              {t('lblRunningSince', { ns: NS, defaultValue: 'En cours' })}
            </p>
            <p className="font-mono text-2xl font-semibold text-emerald-900" aria-live="off">
              {u3147FormatElapsedHms(elapsedSec)}
            </p>
            <p className="text-sm text-emerald-800">{u3147FormatProjectLabel(runningSession?.project as unknown as U3147FindProjectItem)}</p>
          </div>
          {canTrack && (
            <U2000ActionBtn
              label={t('btnStop', { ns: NS, defaultValue: 'Arrêter' })}
              handleClick={() => void handleStop()}
              disabled={busy}
              className="bg-red-600! hover:bg-red-700!"
              title={t('btnStop', { ns: NS, defaultValue: 'Arrêter' })}
            />
          )}
        </div>
      )}

      {!isRunning && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)]">
            <div className="min-w-0 text-left [&_input]:text-left">
              <U2000AutoCompleteInput
                pageSize={15}
                formStateHandlers={formStateHandlers as never}
                fieldName="project"
                mainFieldName="projetNumber"
                entityLink="U3121"
                label={t('colProject', { ns: NS, defaultValue: 'Projet' })}
                onSearch={onSearchProject}
                displayFn={displayProject}
                overrideExtractDataFn={extractProjectData as any}
                resultLabels={[]}
                resultFields={['projetNumber', 'projetName', 'customerName']}
                renderPanel={renderProjectPanel}
                onSetCurrentSelection={bindProjectSelection}
                minCarLaunchSearch={3}
                customSearchPanelHeight={500}
                panelWidth={400}
                dropdownWidth={520}
                basicResultHeight={300}
                readonly={captureReadonly}
                required
              />
            </div>
            <div className="min-w-0">
              <U2000ComboBox
                key={`${project?.id ?? ''}-${subproject?.id ?? ''}-${data?.taskId ?? ''}`}
                fieldName="taskId"
                label={t('colTask', { ns: NS, defaultValue: 'Tâche' })}
                data={tasks}
                keyField="id"
                formatLabel={formatTaskLabel}
                findItemByValue={findTaskByValue}
                formStateHandlers={formStateHandlers}
                readonly={captureReadonly || !hasProjectSelected}
                required
              />
            </div>
            <div className="min-w-0">
              <U2000InputText
                fieldName="note"
                label={t('colNote', { ns: NS, defaultValue: 'Note' })}
                formStateHandlers={formStateHandlers}
                readonly={captureReadonly}
                inlineEnd={noteFieldActions}
              />
            </div>
          </div>
          {admIncludeSubProjectEnabled && project?.includeSubProjet && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="min-w-0 text-left [&_input]:text-left">
                <U2000AutoCompleteInput
                  pageSize={15}
                  formStateHandlers={formStateHandlers as never}
                  fieldName="subproject"
                  mainFieldName="subProjetNumber"
                  label={t('colSubproject', { ns: NS, defaultValue: 'Sous-projet' })}
                  onSearch={onSearchSubproject}
                  displayFn={displaySubproject}
                  overrideExtractDataFn={extractSubprojectData as any}
                  resultLabels={[]}
                  resultFields={['subProjetNumber', 'subProjetName']}
                  onSetCurrentSelection={bindSubprojectSelection}
                  minCarLaunchSearch={0}
                  basicResultHeight={300}
                  readonly={captureReadonly || !hasProjectSelected}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {favoriteProjects.length > 0 && !isRunning && (
        <div className="mt-4 flex flex-wrap gap-2">
          {favoriteProjects.map((rp) => {
            const chipLabel = u3147FormatRecentProjectChipLabel(rp, i18n.language);
            const identityKey = u3147RecentProjectChipKey(rp);
            return (
              <div
                key={identityKey}
                className="inline-flex max-w-full items-stretch overflow-hidden rounded-full border border-gray-300 bg-gray-50 text-xs font-medium text-gray-700"
              >
                <button
                  type="button"
                  disabled={!isSelectedToday}
                  title={chipLabel}
                  className="max-w-[16rem] truncate px-3 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => void applyRecent(rp)}
                >
                  {chipLabel}
                </button>
                <button
                  type="button"
                  aria-label={t('btnRemoveFavorite', { ns: NS, defaultValue: 'Retirer des favoris' })}
                  title={t('btnRemoveFavorite', { ns: NS, defaultValue: 'Retirer des favoris' })}
                  className="border-l border-gray-300 px-2 py-1 text-base leading-none text-gray-500 hover:bg-red-50 hover:text-red-600"
                  onClick={() => handleRemoveFavoriteChip(identityKey)}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default U3147TimerCard;
